import { logger } from '../../utils/logger';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonInput,
  IonItem,
  IonLabel,
  IonPage,
  IonTextarea,
  IonTitle,
  IonToolbar,
  IonList,
  IonListHeader,
  IonSpinner,
  IonToast,
  IonAlert,
  IonCard,
  IonCardContent,
  IonIcon,
  IonNote,
} from '@ionic/react';
import { locationOutline, checkmarkCircle, chevronDown, chevronUp } from 'ionicons/icons';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { profileService } from '../../services/profileService';
import { geocodingService } from '../../services/geocodingService';
import { moderateDisplayName, moderateBio, moderateContent } from "../../services/contentModeration";
import { Profile } from '../../types/profile';
import AvatarBuilder from '../../components/avatar/AvatarBuilder';
import { AvatarPreview } from '../../components/avatar/AvatarPreview';
import { DiceCatAvatar } from '../../components/avatar/avatarTypes';
import { GenderSelection, Gender } from '../../components/GenderSelection';
import { PlayPreferences, PlayPreference, OnlinePlatform } from '../../components/PlayPreferences';
import './EditProfile.css';
import { emitProfileUpdate } from '../../contexts/ProfileContext';
import { updateDataWithRetry } from '../../services/retryService';
import { handleError, ErrorMessages } from '../../services/errorHandler';

const EditProfile: React.FC = () => {
  const history = useHistory();
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [showAvatarBuilder, setShowAvatarBuilder] = useState(false);
  const [geocoding, setGeocoding] = useState(false);
  const [locationVerified, setLocationVerified] = useState(false);
  const [showGenderSection, setShowGenderSection] = useState(false);

  // Form fields
  const [displayName, setDisplayName] = useState('');
  const [pronouns, setPronouns] = useState('');
  const [bio, setBio] = useState('');
  const [locationCity, setLocationCity] = useState('');
  const [locationLat, setLocationLat] = useState<number | null>(null);
  const [locationLng, setLocationLng] = useState<number | null>(null);
  const [maxDistance, setMaxDistance] = useState(25);
  const [avatar, setAvatar] = useState<DiceCatAvatar>({ color: '#1E88E5' });

  // Gender fields
  const [genders, setGenders] = useState<Gender[]>([]);
  const [genderPreferences, setGenderPreferences] = useState<Gender[]>([]);

  // Play preference fields - REQUIRED
  const [playPreferences, setPlayPreferences] = useState<PlayPreference[]>([]);
  const [onlinePlatforms, setOnlinePlatforms] = useState<OnlinePlatform[]>([]);

  useEffect(() => {
    loadProfile();
  }, []);

  const loadProfile = async () => {
    logger.log('Loading profile...');
    const data = await profileService.getCurrentProfile();
    logger.log('Profile data:', data);

    if (data) {
      // Profile exists - load all data
      setProfile(data);
      setDisplayName(data.display_name || '');
      setPronouns(data.pronouns || '');
      setBio(data.bio || '');
      setLocationCity(data.location_city || '');
      setLocationLat(data.location_lat ?? null);
      setLocationLng(data.location_lng ?? null);
      setMaxDistance(data.max_distance_km || 25);
      setAvatar(data.avatar || { color: '#1E88E5' });

      // Load gender fields (both are arrays now)
      setGenders((data.gender as Gender[]) || []);
      setGenderPreferences((data.gender_preference as Gender[]) || []);

      // Load play preference fields
      setPlayPreferences((data.play_preferences as PlayPreference[]) || []);
      setOnlinePlatforms((data.online_platforms as OnlinePlatform[]) || []);

      // Check if location is already verified
      if (data.location_lat && data.location_lng) {
        setLocationVerified(true);
      }
    } else {
      // Profile not found - create it automatically
      logger.log('Ã¢Å¡Â Ã¯Â¸Â Profile not found, creating one...');
      const createResult = await profileService.createProfileIfMissing();

      if (createResult.success) {
        logger.log('Ã¢Å“â€¦ Profile created successfully, reloading...');
        // Reload profile after creation
        const newData = await profileService.getCurrentProfile();

        if (newData) {
          setProfile(newData);
          setDisplayName(newData.display_name || '');
          setAvatar(newData.avatar || { color: '#1E88E5' });
          // Initialize with default values for new profile
          setPronouns('');
          setBio('');
          setLocationCity('');
          setLocationLat(null);
          setLocationLng(null);
          setMaxDistance(25);
          setGenders([]);
          setGenderPreferences([]);
          setPlayPreferences([]);
          setOnlinePlatforms([]);

          // Show success message
          setToastMessage('Profile created! Please complete your information.');
          setShowToast(true);
        } else {
          setErrorMessage('Created profile but failed to load it. Please refresh the page.');
          setShowError(true);
        }
      } else {
        setErrorMessage(`Could not create profile: ${createResult.error || 'Unknown error'}. Please contact support.`);
        setShowError(true);
      }
    }

    setLoading(false);
  };

  const handleGeocodeLocation = async () => {
    if (!locationCity.trim()) {
      setErrorMessage('Please enter a city name');
      setShowError(true);
      return;
    }

    setGeocoding(true);
    setLocationVerified(false);

    try {
      // Simple! Just geocode with Nominatim (free)
      const result = await geocodingService.geocode(locationCity);

      if (result) {
        setLocationLat(result.lat);
        setLocationLng(result.lng);
        setLocationCity(result.city); // Use the standardized city name
        setLocationVerified(true);
        setToastMessage(`Location verified: ${result.displayName}`);
        setShowToast(true);
      } else {
        setErrorMessage('Could not find that location. Try adding country (e.g., "Helsinki, Finland")');
        setShowError(true);
      }
    } catch (error) {
      logger.error('Geocoding error:', error);
      setErrorMessage('Error verifying location. Please try again.');
      setShowError(true);
    }

    setGeocoding(false);
  };

  const handleSave = async () => {
    logger.log('Saving profile...');


    // Validation
    if (!displayName.trim()) {
      setErrorMessage('Display name is required');
      setShowError(true);
      return;
    }

    // No emojis allowed in display name
    const emojiRegex = /[\u{1F300}-\u{1F9FF}|\u{2600}-\u{26FF}|\u{2700}-\u{27BF}]/u;
    if (emojiRegex.test(displayName)) {
      setErrorMessage('Display name cannot contain emojis');
      setShowError(true);
      return;
    }

    if (displayName.length > 30) {
      setErrorMessage('Display name must be 30 characters or less');
      setShowError(true);
      return;
    }

    // Content moderation - check for hate speech and slurs
    const displayNameCheck = moderateDisplayName(displayName);
    if (!displayNameCheck.isClean) {
      setErrorMessage(displayNameCheck.reason || 'Display name contains inappropriate content');
      setShowError(true);
      return;
    }

    // Check pronouns if provided
    if (pronouns.trim()) {
      const pronounsCheck = moderateContent(pronouns);
      if (!pronounsCheck.isClean) {
        setErrorMessage(pronounsCheck.reason || 'Pronouns contain inappropriate content');
        setShowError(true);
        return;
      }
    }

    const bioCheck = moderateBio(bio);
    if (!bioCheck.isClean) {
      setErrorMessage(bioCheck.reason || 'Bio contains inappropriate content');
      setShowError(true);
      return;
    }

    // Location is only required if user plays in-person
    const onlyOnline = playPreferences.length === 1 && playPreferences.includes('online' as PlayPreference);

    if (!onlyOnline) {
      if (!locationCity.trim()) {
        setErrorMessage('Location is required for in-person play');
        setShowError(true);
        return;
      }

      if (!locationVerified || !locationLat || !locationLng) {
        setErrorMessage('Please verify your location using the "Verify Location" button');
        setShowError(true);
        return;
      }
    }

    // REQUIRED: Play preferences validation
    if (playPreferences.length === 0) {
      setErrorMessage('Please select at least one play preference (In-Person or Online)');
      setShowError(true);
      return;
    }

    // If online is selected, require at least one platform
    if (playPreferences.includes('online' as PlayPreference) && onlinePlatforms.length === 0) {
      setErrorMessage('Please select at least one online gaming platform');
      setShowError(true);
      return;
    }

    setSaving(true);

    const updates: Partial<Profile> = {
      display_name: displayName.trim(),
      pronouns: pronouns.trim() || null,
      bio: bio.trim(),
      location_city: locationCity.trim(),
      location_lat: locationLat ?? undefined,
      location_lng: locationLng ?? undefined,
      max_distance_km: maxDistance,
      avatar: avatar,
      // Gender fields (save as arrays)
      gender: genders.length > 0 ? genders : null,
      gender_preference: genderPreferences.length > 0 ? genderPreferences : null,
      // Play preference fields - REQUIRED
      play_preferences: playPreferences,
      online_platforms: onlinePlatforms.length > 0 ? onlinePlatforms : null,
    };

    logger.log('Updating with:', updates);

    try {
      const result = await updateDataWithRetry(
        () => profileService.updateProfile(updates)
      );
      logger.log('Update result:', result);

      if (result.success && result.data) {
        // Update local state with returned data (faster than reloading)
        setProfile(result.data as unknown as Profile);

        // Notify ProfileContext that the profile was updated
        emitProfileUpdate();

        setToastMessage('Profile updated successfully!');
        setShowToast(true);
        setTimeout(() => {
          history.goBack();
        }, 1000);
      } else {
        const errorMsg = result.error?.userMessage || ErrorMessages.PROFILE_UPDATE_FAILED;
        setErrorMessage(errorMsg);
        setShowError(true);
      }
    } catch (error) {
      const appError = handleError(error, 'update profile');
      setErrorMessage(appError.userMessage);
      setShowError(true);
    }

    setSaving(false);
  };

  const handleManageGames = () => {
    history.push('/profile/games');
  };

  const handleManageAvailability = () => {
    history.push('/profile/availability');
  };

  const handleAvatarSave = (newAvatar: DiceCatAvatar) => {
    setAvatar(newAvatar);
    setToastMessage('Avatar updated! Remember to save your profile.');
    setShowToast(true);
  };

  if (loading) {
    return (
      <IonPage>
        <IonHeader>
          <IonToolbar>
            <IonButtons slot="start">
              <IonBackButton />
            </IonButtons>
            <IonTitle>Edit Profile</IonTitle>
          </IonToolbar>
        </IonHeader>
        <IonContent className="ion-padding">
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '50px' }}>
            <IonSpinner />
          </div>
        </IonContent>
      </IonPage>
    );
  }

  return (
    <IonPage>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonBackButton defaultHref="/tabs/tab3" />
          </IonButtons>
          <IonTitle>Edit Profile</IonTitle>
          <IonButtons slot="end">
            <IonButton onClick={handleSave} disabled={saving}>
              {saving ? <IonSpinner /> : 'Save'}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>

      <IonContent
        fullscreen
        style={{
          '--padding-bottom': 'calc(env(safe-area-inset-bottom) + 80px)'
        }}
      >
        <div className="edit-profile-container">
          {!profile && (
            <IonItem color="danger">
              <IonLabel className="ion-text-wrap" style={{ color: '#000' }}>
                Profile not found. Please contact support.
              </IonLabel>
            </IonItem>
          )}

          {/* Avatar Section */}
          <IonList>
            <IonListHeader>
              <IonLabel>Your Gaming Avatar</IonLabel>
            </IonListHeader>
            <IonCard>
              <IonCardContent style={{ textAlign: 'center' }}>
                <div style={{ padding: '20px 0' }}>
                  <AvatarPreview avatarData={avatar} size={120} userId={profile?.id || ''} isAdmin={profile?.is_admin || false} />
                </div>
                <IonButton
                  expand="block"
                  onClick={() => setShowAvatarBuilder(true)}
                  color="primary"
                >
                  Customize Avatar
                </IonButton>
                <p className="avatar-tip">
                  Create your unique gaming persona with custom colors and accessories
                </p>
              </IonCardContent>
            </IonCard>
          </IonList>

          {/* Basic Information */}
          <IonList>
            <IonListHeader>
              <IonLabel>Basic Information</IonLabel>
            </IonListHeader>

            <IonItem>
              <IonLabel position="stacked">Display Name *</IonLabel>
              <IonInput
                value={displayName}
                onIonInput={(e) => setDisplayName(e.detail.value!)}
                placeholder="How should others see your name?"
                autocapitalize="words"
                maxlength={30}
              />
              <div className="char-count">{displayName.length}/30</div>
              <IonNote style={{ fontSize: "12px", color: "var(--ion-color-medium)", marginTop: "4px" }}>
                No emojis allowed - use letters, numbers, and basic punctuation only
              </IonNote>
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Pronouns</IonLabel>
              <IonInput
                value={pronouns}
                onIonInput={(e) => setPronouns(e.detail.value!)}
                placeholder="e.g., he/him, she/her, they/them"
                maxlength={20}
              />
            </IonItem>

            <IonItem>
              <IonLabel position="stacked">Bio</IonLabel>
              <IonTextarea
                value={bio}
                onIonInput={(e) => setBio(e.detail.value!)}
                placeholder="Tell us about yourself and your gaming interests..."
                rows={4}
                maxlength={500}
                autocapitalize="sentences"
              />
              <div className="char-count">{bio.length}/500</div>
            </IonItem>
          </IonList>

          {/* Play Preferences Section - REQUIRED, ALWAYS VISIBLE */}
          <IonList>
            <IonListHeader>
              <IonLabel>
                Play Style Preferences *
                <p style={{
                  fontSize: '12px',
                  fontWeight: 'normal',
                  color: 'var(--ion-color-medium)',
                  marginTop: '4px'
                }}>
                  Required - Select at least one option
                </p>
              </IonLabel>
            </IonListHeader>
            <PlayPreferences
              currentPreferences={playPreferences}
              currentPlatforms={onlinePlatforms}
              onPreferencesChange={setPlayPreferences}
              onPlatformsChange={setOnlinePlatforms}
            />
          </IonList>

          {/* Gender & Comfort Section - Optional */}
          <div className="gender-comfort-section" style={{ margin: '16px 0' }}>
            <div
              className="collapsible-header"
              onClick={() => setShowGenderSection(!showGenderSection)}
              style={{
                background: 'var(--ion-background-color)',
                border: '1px solid var(--ion-color-light)'
              }}
            >
              <IonLabel>
                <strong>Gender & Comfort Preferences (Optional)</strong>
                {(genders.length > 0 || genderPreferences.length > 0) && (
                  <p style={{ fontSize: '12px', color: 'var(--ion-color-medium)', margin: '4px 0 0 0' }}>
                    {genders.length > 0 && `${genders.length} selected`}
                    {genders.length > 0 && genderPreferences.length > 0 && ' â€¢ '}
                    {genderPreferences.length > 0 && `Comfortable with ${genderPreferences.length}`}
                  </p>
                )}
              </IonLabel>
              <IonIcon
                icon={showGenderSection ? chevronUp : chevronDown}
                style={{ fontSize: '24px' }}
              />
            </div>
            {showGenderSection && (
              <div className="gender-comfort-card">
                <GenderSelection
                  currentGenders={genders}
                  currentPreferences={genderPreferences}
                  onGendersChange={setGenders}
                  onPreferencesChange={setGenderPreferences}
                />
              </div>
            )}
          </div>

          {/* Location Section */}
          <IonList>
            <IonListHeader>
              <IonLabel>
                Location
                {playPreferences.length === 1 && playPreferences.includes('online' as PlayPreference) ? (
                  <p style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    color: 'var(--ion-color-medium)',
                    marginTop: '4px'
                  }}>
                    Optional for online-only play
                  </p>
                ) : (
                  <p style={{
                    fontSize: '12px',
                    fontWeight: 'normal',
                    color: 'var(--ion-color-medium)',
                    marginTop: '4px'
                  }}>
                    Required for in-person play
                  </p>
                )}
              </IonLabel>
            </IonListHeader>

            <IonItem>
              <IonLabel position="stacked">
                City {!(playPreferences.length === 1 && playPreferences.includes('online' as PlayPreference)) && '*'}
              </IonLabel>
              <IonInput
                value={locationCity}
                onIonInput={(e) => {
                  setLocationCity(e.detail.value!);
                  setLocationVerified(false); // Reset verification when city changes
                }}
                placeholder="e.g., Helsinki, Portland, Tokyo"
                autocapitalize="words"
              />
            </IonItem>

            <IonCard style={{ margin: '16px' }}>
              <IonCardContent>
                {locationVerified ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--ion-color-success)' }}>
                    <IonIcon icon={checkmarkCircle} />
                    <span>Location verified</span>
                    {locationLat && locationLng && (
                      <IonNote style={{ marginLeft: '8px' }}>
                        ({locationLat.toFixed(4)}, {locationLng.toFixed(4)})
                      </IonNote>
                    )}
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '14px', marginBottom: '12px' }}>
                      Click below to verify your location. This helps us find gaming partners near you.
                    </p>
                    <IonButton
                      expand="block"
                      onClick={handleGeocodeLocation}
                      disabled={!locationCity.trim() || geocoding}
                    >
                      {geocoding ? (
                        <>
                          <IonSpinner style={{ marginRight: '8px' }} />
                          Verifying...
                        </>
                      ) : (
                        <>
                          <IonIcon icon={locationOutline} slot="start" />
                          Verify Location
                        </>
                      )}
                    </IonButton>
                  </>
                )}
              </IonCardContent>
            </IonCard>

            <IonItem>
              <IonLabel position="stacked">
                Max Travel Distance: {maxDistance}km
              </IonLabel>
              <input
                type="range"
                min="5"
                max="100"
                step="5"
                value={maxDistance}
                onChange={(e) => setMaxDistance(parseInt(e.target.value))}
                className="distance-slider"
              />
            </IonItem>
          </IonList>

          {/* Gaming Preferences */}
          <IonList>
            <IonListHeader>
              <IonLabel>Gaming Preferences</IonLabel>
            </IonListHeader>

            <IonItem button onClick={handleManageGames}>
              <IonLabel>
                <h2>Manage Games</h2>
                <p>Add your favorite board games</p>
              </IonLabel>
            </IonItem>

            <IonItem button onClick={handleManageAvailability}>
              <IonLabel>
                <h2>Set Availability</h2>
                <p>When are you free to play?</p>
              </IonLabel>
            </IonItem>
          </IonList>

          {/* Save Button */}
          <div className="save-section">
            <IonButton
              expand="block"
              onClick={handleSave}
              disabled={saving || !profile}
            >
              {saving ? <IonSpinner /> : 'Save Changes'}
            </IonButton>
          </div>
        </div>

        {/* Avatar Builder Modal */}
        <AvatarBuilder
          isOpen={showAvatarBuilder}
          onDismiss={() => setShowAvatarBuilder(false)}
          onSave={handleAvatarSave}
          currentAvatar={avatar}
        />

        {/* Success Toast */}
        <IonToast
          isOpen={showToast}
          onDidDismiss={() => setShowToast(false)}
          message={toastMessage}
          duration={2000}
          color="success"
          aria-live="polite"
        />

        {/* Error Alert */}
        <IonAlert
          isOpen={showError}
          onDidDismiss={() => setShowError(false)}
          header="Error"
          message={errorMessage}
          buttons={['OK']}
        />
      </IonContent>
    </IonPage>
  );
};

export default EditProfile;